import ABCyaPhaser from '@abcya/abcya_phaser';
import Phaser from 'phaser';
import FriendChooseDisplay from '../game/FriendChooseDisplay';
import MessageGuy from '../game/MessageGuy';
import MessageThoughtBubble from '../game/MessageThoughtBubble';
import ResultsDisplay from '../game/ResultsDisplay';
import getUserData from '../game/UserData';
import UIAssetKeys from '../UIAssetKeys';
import AquariumScreen from './AquariumScreen';
import CountFishScreen from './CountFishScreen';
import LocalizationSoundAssetKeys from '../Localization-SoundAssetKeys';
import FontKeys from '../FontKeys';
import ABCyaLib from '@abcya/abcya_lib';

export default class ResultScreen extends Phaser.Scene {
  static get KEY() { return 'ResultScreen'; }

  constructor() {
    super({ key: ResultScreen.KEY });
  }

  init() {
    this.resultsDisplay = null;

    this.messageGuy = null;

    this.btnGo = null;
    this.goPulseTween = false;

    this.friendSelector = null;

    this.myScore = null;

    this.userdata = getUserData();

    // uncomment if you want to test certain parts of the results
    // this.userdata.roundsFinished = true;
  }

  create() {
    ABCyaPhaser.Audio.playOneShot(LocalizationSoundAssetKeys._counting_fish_13, this, 1, false, 0, ABCyaPhaser.Audio.LOCALIZATION_AUDIO_SPRITE);

    this.resultsDisplay = new ResultsDisplay(this);
    this.resultsDisplay.once('RESULTS_CHART_SHOWN', this.showScorePercent, this);
    this.add.existing(this.resultsDisplay);

    this.messageGuy = new MessageGuy(this);
    this.add.existing(this.messageGuy);

    this.thoughtBubble = new MessageThoughtBubble(this);

    this.btnGo = this.add.container(this.scale.width / 2, 0);
    ABCyaPhaser.Images.addImage(0, 0, UIAssetKeys._BtnGo, this.btnGo);
    ABCyaPhaser.Text.addText(0, 0, 56, 0xffffff, FontKeys._bpreplaybold, ABCyaPhaser.Localization.instance.getLocalizedString('go'), this.btnGo);

    this.btnGo.height = this.btnGo.getBounds().height;
    this.btnGo.setAlpha(0);
    ABCyaPhaser.Container.setContainerInteractive(this.btnGo);

    this.layoutRatio();
  }

  showScorePercent () {
    this.thoughtBubble.removeAllListeners();
    this.add.existing(this.thoughtBubble);
    const s = this.userdata.getCurrentScore() ?? 65; // can change the "?? ##" depending on if you want to see the success or fail result screen
    this.myScore = s; // Can't grab from current if we level up
    if (s >= 70) {
      this.userdata.levelUp();
      this.thoughtBubble.once('THOUGHT_OVER', this.readyForNextRound, this);
    }
    else {
      this.thoughtBubble.once('THOUGHT_OVER', this.displayTryAgain, this);
    }
    const percentText = `${s + (ABCyaLib.Environment.instance.gameConfig.includeSpaceBeforePercentSymbol ? ' ' : '')}%`;
    this.thoughtBubble.initForThought(percentText);
  }

  readyForNextRound () {
    if (this.userdata.roundsFinished) {
      const msg = ABCyaPhaser.Localization.instance.getLocalizedString('finished_three');
      this.messageGuy.showMessage(msg,18,true);
      this.tweens.killTweensOf(this.resultsDisplay);
      ABCyaPhaser.Tweens.addTween(this.resultsDisplay, 160, { alpha: 0 }, Phaser.Math.Easing.Quadratic.Out);
      ABCyaPhaser.Tweens.addTween(this.btnGo, 420, { alpha: 1 }, Phaser.Math.Easing.Quadratic.Out);
      this.btnGo.once(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, this.prepareForPlay, this);
      ABCyaPhaser.Audio.playOneShot(LocalizationSoundAssetKeys._counting_fish_01, this, 1, false, 0, ABCyaPhaser.Audio.LOCALIZATION_AUDIO_SPRITE);
    }
    else {
      ABCyaPhaser.Audio.playOneShot(LocalizationSoundAssetKeys._counting_fish_15, this, 1, false, 0, ABCyaPhaser.Audio.LOCALIZATION_AUDIO_SPRITE);
      let msg = '';
      msg = ABCyaPhaser.Localization.instance.getLocalizedString('clam_good_score');
      this.friendSelector = new FriendChooseDisplay(this);
      this.friendSelector.on('FRIEND_SELECTED', this.friendClicked, this);
      this.friendSelector.x = this.scale.width / 2 - this.friendSelector.getBounds().width / 2;
      this.friendSelector.y = 40;
      this.add.existing(this.friendSelector);

      this.messageGuy.showMessage(msg,18,true);
      this.tweens.killTweensOf(this.resultsDisplay);
      ABCyaPhaser.Tweens.addTween(this.resultsDisplay, 200, { alpha: 0 }, Phaser.Math.Easing.Quadratic.Out);
      ABCyaPhaser.Tweens.addTween(this.btnGo, 420, { alpha: 1 }, Phaser.Math.Easing.Quadratic.Out);

      this.btnGo.once(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, this.nextRound, this);
    }
  }

  prepareForPlay () {
    this.scene.start(AquariumScreen.KEY);
  }

  friendClicked () {
    if (!this.goPulseTween) {
      this.pulseGoButton();
    }
  }

  pulseGoButton () {
    if (!this.goPulseTween) {

      this.goPulseTween = true;
      this.createGoBtnPulseTween();
    }
  }

  async createGoBtnPulseTween() {
    await ABCyaPhaser.Async.waitForTween(ABCyaPhaser.Tweens.addTween(this.btnGo, 840, { scale: 0.8, completeDelay: 120 }, Phaser.Math.Easing.Quadratic.In));
    await ABCyaPhaser.Async.waitForTween(ABCyaPhaser.Tweens.addTween(this.btnGo, 800, { scale: 1, completeDelay: 3740 }, Phaser.Math.Easing.Quadratic.Out));

    // stop repeating once this scene is no longer active
    if (this.scene.isActive()) this.createGoBtnPulseTween();
  }

  nextRound () {
    if (this.friendSelector) {
      const selectedFriendID = this.friendSelector.getSelectedFriendID();
      if (selectedFriendID >= 0) this.userdata.addFriend(selectedFriendID);
    }

    this.scene.start(CountFishScreen.KEY);
  }

  displayTryAgain () {
    ABCyaPhaser.Audio.playOneShot(LocalizationSoundAssetKeys._counting_fish_14, this, 1, false, 0, ABCyaPhaser.Audio.LOCALIZATION_AUDIO_SPRITE);
    const msg = ABCyaPhaser.Localization.instance.getLocalizedString('clam_bad_score');
    this.messageGuy.showMessage(msg,16,true);
    this.tweens.killTweensOf(this.resultsDisplay);
    ABCyaPhaser.Tweens.addTween(this.resultsDisplay, 1200, { alpha: 0 }, Phaser.Math.Easing.Quadratic.Out);
    this.btnGo.once(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, this.startOver, this);
    this.btnGo.alpha = 0;
    ABCyaPhaser.Tweens.addTween(this.btnGo, 1330, { alpha: 1, completeDelay: 200 }, Phaser.Math.Easing.Quadratic.Out, () => this.pulseGoButton());
  }

  startOver () {
    this.scene.start(CountFishScreen.KEY);
  }

  layoutRatio () {
    this.messageGuy.x = this.scale.width - this.messageGuy.getBounds().width - 20;
    this.messageGuy.y = this.scale.height - this.messageGuy.getBounds().height - 15;

    this.resultsDisplay.x = this.messageGuy.x - this.resultsDisplay.workingWidth * this.resultsDisplay.scaleX + 20;
    const dbuf = 85;
    this.resultsDisplay.y = this.scale.height - (this.resultsDisplay.getBounds().height * this.resultsDisplay.scaleY) - dbuf;

    this.thoughtBubble.x = this.messageGuy.x + this.thoughtBubble.getBounds().width / 2;
    this.thoughtBubble.y = this.messageGuy.y - this.thoughtBubble.getBounds().height / 2 - 20;

    this.btnGo.x = this.resultsDisplay.x + (this.resultsDisplay.workingWidth * this.resultsDisplay.scaleX) / 2;
    this.btnGo.y = this.scale.height - this.btnGo.height / 2 - 15;
  }
}